import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Sidebar } from "../components/Sidebar";
import { LoadingState, ErrorState } from "../components/UI";
import { getPerformanceDeviation, parseConfiguredExpected } from "../utils/performanceMetrics";

const API_URL = process.env.REACT_APP_API_URL || "/api";

function getPreviousMonthYYYYMM() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

const DEFAULT_MONTH = getPreviousMonthYYYYMM();
const SURPERF_DARK_GREEN = "#4DC455";
const GLOBAL_METRIC_PILL_WIDTH_CLASS = "shrink-0 whitespace-nowrap";

const BASE_METRICS = [
  {
    key: "gm_guest_program_rate",
    targetKey: "target_gm_guest_program_rate",
    shortLabel: "Guests",
    unit: "%",
  },
  {
    key: "gm_actions_rate",
    targetKey: "target_gm_actions_rate",
    shortLabel: "Actions",
    unit: "%",
  },
  {
    key: "gm_rewards_rate",
    targetKey: "target_gm_rewards_rate",
    shortLabel: "Récomp.",
    unit: "%",
  },
];

const LUNISHOWER_METRICS = [
  { key: "ls_roi_rate", targetKey: "target_ls_roi_rate", shortLabel: "ROI LS", unit: "%" },
  {
    key: "ls_conso_douches_decrease_rate",
    targetKey: "target_ls_conso_douches_decrease_rate",
    shortLabel: "Écon. eau",
    unit: "%",
  },
  {
    key: "ls_functional_modules_rate",
    targetKey: "target_ls_functional_modules_rate",
    shortLabel: "Modules LS",
    unit: "%",
  },
];

const LUNICLEAN_METRICS = [
  { key: "lc_roi_rate", targetKey: "target_lc_roi_rate", shortLabel: "ROI LC", unit: "%" },
];

const ALL_METRICS = [...BASE_METRICS, ...LUNISHOWER_METRICS, ...LUNICLEAN_METRICS];

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getLuniCleanRoiRate(totalSavingsEur, roomCount, pricePerRoom) {
  const savings = safeNumber(totalSavingsEur);
  const rooms = safeNumber(roomCount);
  const price = safeNumber(pricePerRoom);
  if (price <= 0 || rooms <= 0) return null;
  const savingsPerRoom = savings / rooms;
  return ((savingsPerRoom - price) / price) * 100;
}

function getStatusFromDeviation(deviation) {
  if (deviation === null) return { label: "N/A" };
  if (deviation <= -30) return { label: "EN ALERTE" };
  if (deviation <= -10) return { label: "À SURVEILLER" };
  if (deviation >= 10) return { label: "SURPERF" };
  return { label: "OK" };
}

function computeHotelSummary(hotelRow) {
  const actualByMetric = {
    gm_guest_program_rate:
      hotelRow.actual_gm_guest_program_rate ??
      (hotelRow.greenmiles_percentage == null ? null : safeNumber(hotelRow.greenmiles_percentage)),
    gm_actions_rate:
      hotelRow.actual_gm_actions_rate ??
      (hotelRow.guest_program_rate == null ? null : safeNumber(hotelRow.guest_program_rate)),
    gm_rewards_rate:
      hotelRow.actual_gm_rewards_rate ??
      (hotelRow.rewards_used_rate == null ? null : safeNumber(hotelRow.rewards_used_rate)),
    ls_roi_rate: hotelRow.actual_ls_roi_rate ?? null,
    ls_conso_douches_decrease_rate: hotelRow.actual_ls_conso_douches_decrease_rate ?? null,
    ls_functional_modules_rate:
      hotelRow.actual_ls_functional_modules_rate ??
      (hotelRow.ls_functional_modules_rate == null ? null : safeNumber(hotelRow.ls_functional_modules_rate)),
    lc_roi_rate:
      hotelRow.actual_lc_roi_rate ??
      getLuniCleanRoiRate(
        hotelRow.lc_total_savings_eur,
        hotelRow.hotel_rooms_with_qr_count ?? hotelRow.hotel_rooms_count,
        hotelRow.target_lc_price_per_room ?? 2
      ),
  };

  const hasGreenMiles = Number(hotelRow.has_greenmiles) === 1;
  const hasLuniShower = Number(hotelRow.has_lunishower) === 1;

  const isMetricApplicable = (metricKey) => {
    if (BASE_METRICS.some((m) => m.key === metricKey)) return hasGreenMiles;
    if (LUNICLEAN_METRICS.some((m) => m.key === metricKey)) return hasGreenMiles;
    if (LUNISHOWER_METRICS.some((m) => m.key === metricKey)) return hasLuniShower;
    return false;
  };

  const metricRows = ALL_METRICS.map((metric) => {
    const isApplicable = isMetricApplicable(metric.key);
    const expected = isApplicable ? parseConfiguredExpected(hotelRow[metric.targetKey]) : null;
    const isEnabled = isApplicable && expected !== null;
    const rawActual = isApplicable ? actualByMetric[metric.key] : null;
    const actual = rawActual === null || rawActual === undefined ? null : safeNumber(rawActual);
    const deviation = getPerformanceDeviation(expected, actual);
    const status = getStatusFromDeviation(deviation);
    return { ...metric, expected, actual, deviation, status, isEnabled, isApplicable };
  });

  const enabledRows = metricRows.filter((row) => row.isEnabled);
  const comparableRows = enabledRows.filter((row) => row.deviation !== null);
  const enabledTotal = enabledRows.length;
  const goodCount = enabledRows.filter((row) => row.status.label === "OK" || row.status.label === "SURPERF").length;
  const goodRatio = enabledTotal > 0 ? (goodCount / enabledTotal) * 100 : 0;

  const redCount = comparableRows.filter((row) => row.status.label === "EN ALERTE").length;
  const orangeCount = comparableRows.filter((row) => row.status.label === "À SURVEILLER").length;
  const okCount = comparableRows.filter((row) => row.status.label === "OK").length;
  const surperfCount = comparableRows.filter((row) => row.status.label === "SURPERF").length;

  let globalStatus;
  if (comparableRows.length === 0) {
    globalStatus = { label: "N/A", className: "bg-gray-100 text-gray-700" };
  } else if (redCount > 0) {
    globalStatus = { label: "ALERTE", className: "bg-red-100 text-red-700" };
  } else if (orangeCount > 0) {
    globalStatus = { label: "A SURVEILLER", className: "bg-orange-100 text-orange-700" };
  } else if (surperfCount > 0 && okCount === 0) {
    globalStatus = { label: "SURPERF", className: "bg-green-200 text-green-900" };
  } else {
    globalStatus = { label: "OK", className: "bg-green-100 text-green-700" };
  }

  return {
    stats: {
      goodCount,
      total: enabledTotal,
      expectedConfiguredTotal: enabledRows.length,
      goodRatio,
    },
    rowsByKey: Object.fromEntries(metricRows.map((row) => [row.key, row])),
    globalStatus,
  };
}

function isLiveHotel(hotel) {
  return hotel?.hTags === "Live";
}

function getHotelStatusLabel(hotel) {
  if (Number(hotel?.is_equipped) === 0) return "Pas équipé";
  return hotel?.hTags || "—";
}

function computeLivePortfolioSynthesis(liveHotels) {
  const totalEnabled = liveHotels.reduce((sum, hotel) => sum + (hotel.summary?.stats?.total || 0), 0);
  const totalAchieved = liveHotels.reduce((sum, hotel) => sum + (hotel.summary?.stats?.goodCount || 0), 0);
  const globalRatio = totalEnabled > 0 ? (totalAchieved / totalEnabled) * 100 : null;
  return { totalEnabled, totalAchieved, globalRatio };
}

function getRowBgClass(label) {
  if (label === "ALERTE") return "bg-red-200 hover:bg-red-200";
  if (label === "A SURVEILLER") return "bg-orange-200 hover:bg-orange-200";
  if (label === "OK") return "bg-green-200 hover:bg-green-200";
  if (label === "SURPERF") return "";
  if (label === "N/A") return "bg-gray-100 hover:bg-gray-100";
  return "bg-white hover:bg-gray-50";
}

function getCellBgClass(label) {
  if (label === "ALERTE") return "bg-red-200";
  if (label === "A SURVEILLER") return "bg-orange-200";
  if (label === "OK") return "bg-green-200";
  if (label === "SURPERF") return "";
  if (label === "N/A") return "bg-gray-100";
  return "bg-white";
}

function getRowStyle(label) {
  if (label === "SURPERF") return { backgroundColor: SURPERF_DARK_GREEN, color: "#111827" };
  return undefined;
}

function getCellStyle(label) {
  if (label === "SURPERF") return { backgroundColor: SURPERF_DARK_GREEN, color: "#111827" };
  return undefined;
}

function getActualValueTextClass(statusLabel) {
  if (statusLabel === "EN ALERTE") return "text-red-700";
  if (statusLabel === "À SURVEILLER" || statusLabel === "A SURVEILLER") return "text-orange-700";
  if (statusLabel === "OK") return "text-green-800";
  if (statusLabel === "SURPERF") return "text-green-700";
  return "text-gray-600";
}

function formatCompactMetricValue(value, unit = "%", fractionDigits = 1) {
  if (value === null || value === undefined) return "-";
  const n = safeNumber(value);
  if (unit === "%") return `${n.toFixed(fractionDigits)}`;
  return `${n.toLocaleString("fr-FR")}`;
}

function formatCompactExpectedActual(metric) {
  if (!metric) return "-";
  const unit = metric.unit || "%";
  const expected =
    metric.expected === null || metric.expected === undefined
      ? "-"
      : `${formatCompactMetricValue(metric.expected, unit, 1)}${unit}`;
  const actual =
    metric.actual === null || metric.actual === undefined
      ? "-"
      : `${formatCompactMetricValue(metric.actual, unit, 1)}${unit}`;
  const actualClassName = getActualValueTextClass(metric.status?.label);
  const useDarkGreen = metric.status?.label === "OK";

  return (
    <span className="inline-flex items-baseline gap-1 tabular-nums leading-none">
      <span className={actualClassName} style={useDarkGreen ? { color: SURPERF_DARK_GREEN } : undefined}>
        {actual}
      </span>
      <span className="text-gray-400">/</span>
      <span className="text-gray-800">{expected}</span>
    </span>
  );
}

function ObjectivesCell({ hotel }) {
  if (hotel.summary.stats.total === 0) {
    return <span className="text-gray-500 text-xs">-</span>;
  }
  return (
    <span className="inline-flex h-9 items-center rounded-full border border-white/80 bg-white/90 px-3 py-1.5 text-[11.5px] font-bold tabular-nums text-gray-900 shadow-sm">
      {hotel.summary.stats.goodCount}/{hotel.summary.stats.total}&nbsp;(
      {hotel.summary.stats.goodRatio.toFixed(0)}%)
    </span>
  );
}

function getSortableValue(hotel, sortKey) {
  if (sortKey === "hName") return String(hotel.hName || "").toLowerCase();
  if (sortKey === "status") return getHotelStatusLabel(hotel).toLowerCase();
  if (sortKey === "objectives") return hotel.summary?.stats?.goodRatio ?? 0;
  const metricRow = hotel.summary?.rowsByKey?.[sortKey];
  if (!metricRow || metricRow.isEnabled !== true) return -Infinity;
  if (metricRow.actual !== null && metricRow.actual !== undefined) return metricRow.actual;
  return -Infinity;
}

function sortHotels(hotels, sortConfig) {
  if (!sortConfig?.key) return hotels;
  return [...hotels].sort((a, b) => {
    const aValue = getSortableValue(a, sortConfig.key);
    const bValue = getSortableValue(b, sortConfig.key);
    if (sortConfig.key === "hName" || sortConfig.key === "status") {
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    }
    const aNum = typeof aValue === "number" ? aValue : parseFloat(aValue) || 0;
    const bNum = typeof bValue === "number" ? bValue : parseFloat(bValue) || 0;
    if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
    if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
}

const SortableHeader = ({ sortKey, sortConfig, onSort, children }) => {
  const handleSort = (event) => {
    event.stopPropagation();
    onSort(sortKey);
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSort(sortKey);
  };

  return (
    <th
      className="w-px whitespace-nowrap px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-100"
      onClick={handleSort}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="columnheader"
      aria-sort={sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <span className="inline-flex items-center justify-center gap-1">
        {children}
        <span className="text-[10px] font-normal text-gray-400" aria-hidden="true">
          {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </span>
    </th>
  );
};

function PerformanceHotelsTable({
  hotels,
  showStatusColumn = false,
  showSynthesisRow = false,
  synthesis = null,
  sortConfig,
  onSort,
  onNavigateHotel,
}) {
  if (hotels.length === 0 && !showSynthesisRow) {
    return <p className="px-4 py-6 text-center text-sm text-gray-500">Aucun hôtel à afficher pour cette section.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader sortKey="hName" sortConfig={sortConfig} onSort={onSort}>
              Hôtel
            </SortableHeader>
            {showStatusColumn && (
              <SortableHeader sortKey="status" sortConfig={sortConfig} onSort={onSort}>
                Statut
              </SortableHeader>
            )}
            <SortableHeader sortKey="objectives" sortConfig={sortConfig} onSort={onSort}>
              Objectifs
            </SortableHeader>
            {ALL_METRICS.map((metric) => (
              <SortableHeader key={metric.key} sortKey={metric.key} sortConfig={sortConfig} onSort={onSort}>
                <span className="inline-flex flex-col items-center leading-tight">
                  <span>{metric.shortLabel}</span>
                  <span className="text-[10px] font-semibold tracking-normal text-gray-500 normal-case">
                    réelle/attendue
                  </span>
                </span>
              </SortableHeader>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {showSynthesisRow && synthesis && (
            <tr className="bg-slate-100 font-semibold">
              <td
                colSpan={showStatusColumn ? 2 : 1}
                className="whitespace-nowrap px-3 py-3 text-center text-xs uppercase tracking-wide text-gray-700"
              >
                Synthèse portefeuille Live
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-center text-gray-900">
                {synthesis.totalEnabled === 0 ? (
                  <span className="text-gray-500 text-xs">-</span>
                ) : (
                  <span className="inline-flex h-9 items-center rounded-full border border-gray-300 bg-white px-3 py-1.5 text-[11.5px] font-bold tabular-nums shadow-sm">
                    {synthesis.totalAchieved}/{synthesis.totalEnabled}&nbsp;(
                    {synthesis.globalRatio === null ? "-" : `${synthesis.globalRatio.toFixed(0)}%`})
                  </span>
                )}
              </td>
              {ALL_METRICS.map((metric) => (
                <td key={metric.key} className="px-3 py-3 text-center text-xs text-gray-400">
                  —
                </td>
              ))}
            </tr>
          )}
          {hotels.map((hotel) => {
            const statusLabel = hotel.summary.globalStatus.label;
            const rowBgClass = getRowBgClass(statusLabel);
            const cellBgClass = getCellBgClass(statusLabel);
            const rowsByKey = hotel.summary.rowsByKey || {};

            return (
              <tr
                key={hotel.hId}
                className={`cursor-pointer ${rowBgClass}`}
                style={getRowStyle(statusLabel)}
                onClick={() => onNavigateHotel(hotel)}
              >
                <td
                  className={`w-px whitespace-nowrap px-3 py-2 text-center text-xs font-medium text-gray-900 ${cellBgClass}`}
                  style={getCellStyle(statusLabel)}
                >
                  {hotel.hName}
                </td>
                {showStatusColumn && (
                  <td
                    className={`w-px whitespace-nowrap px-3 py-2 text-center text-xs text-gray-700 ${cellBgClass}`}
                    style={getCellStyle(statusLabel)}
                  >
                    {getHotelStatusLabel(hotel)}
                  </td>
                )}
                <td
                  className={`w-px whitespace-nowrap px-3 py-2 text-center align-middle ${cellBgClass}`}
                  style={getCellStyle(statusLabel)}
                >
                  <div className="flex h-9 items-center justify-center">
                    <ObjectivesCell hotel={hotel} />
                  </div>
                </td>
                {ALL_METRICS.map((metric) => {
                  const row = rowsByKey[metric.key];
                  const shouldDash = !row || row.isEnabled !== true;
                  return (
                    <td
                      key={metric.key}
                      className={`w-px whitespace-nowrap px-3 py-2 text-center align-middle ${cellBgClass}`}
                      style={getCellStyle(statusLabel)}
                    >
                      <div className="flex h-9 items-center justify-center">
                        {shouldDash ? (
                          <span className="text-gray-500 text-xs">-</span>
                        ) : (
                          <span
                            className={`inline-flex h-9 items-center rounded-full border border-white/80 bg-white/70 px-3 py-1.5 text-gray-800 shadow-sm ${GLOBAL_METRIC_PILL_WIDTH_CLASS}`}
                          >
                            <span className="text-[11.5px] font-bold leading-none tabular-nums">
                              {formatCompactExpectedActual(row)}
                            </span>
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PerformancesPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [liveSortConfig, setLiveSortConfig] = useState({ key: null, direction: "asc" });
  const [otherSortConfig, setOtherSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    let isCancelled = false;

    const fetchPerformance = async () => {
      if (!token) {
        setLoading(false);
        setRows([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const requestUrl = `${API_URL}/greenmiles/performance/base?month=${encodeURIComponent(selectedMonth)}`;
        const response = await fetch(requestUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.success !== "OK") {
          throw new Error(payload?.message || `Erreur API (${response.status})`);
        }

        if (!isCancelled) {
          setRows(Array.isArray(payload.hotels) ? payload.hotels : []);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || "Impossible de charger les performances.");
          setRows([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchPerformance();
    return () => {
      isCancelled = true;
    };
  }, [token, selectedMonth]);

  const hotelsWithSummary = useMemo(
    () => rows.map((hotel) => ({ ...hotel, summary: computeHotelSummary(hotel) })),
    [rows]
  );

  const filterBySearch = useCallback(
    (hotels) => {
      const normalizedSearch = searchTerm.trim().toLowerCase();
      if (!normalizedSearch) return hotels;
      return hotels.filter((hotel) => String(hotel.hName || "").toLowerCase().includes(normalizedSearch));
    },
    [searchTerm]
  );

  const liveHotels = useMemo(
    () => sortHotels(filterBySearch(hotelsWithSummary.filter(isLiveHotel)), liveSortConfig),
    [hotelsWithSummary, filterBySearch, liveSortConfig]
  );

  const otherHotels = useMemo(
    () => sortHotels(filterBySearch(hotelsWithSummary.filter((h) => !isLiveHotel(h))), otherSortConfig),
    [hotelsWithSummary, filterBySearch, otherSortConfig]
  );

  const liveSynthesis = useMemo(() => computeLivePortfolioSynthesis(liveHotels), [liveHotels]);

  const handleNavigateHotel = useCallback(
    (hotel) => {
      navigate(`/hotels/${hotel.hId}?tab=performance&month=${encodeURIComponent(selectedMonth)}`, {
        state: { hotel: { id: hotel.hId, name: hotel.hName } },
      });
    },
    [navigate, selectedMonth]
  );

  const handleLiveSort = useCallback((key) => {
    setLiveSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const handleOtherSort = useCallback((key) => {
    setOtherSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  if (loading) {
    return <LoadingState message="Chargement des performances hôtels..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  const totalDisplayed = liveHotels.length + otherHotels.length;

  return (
    <div className="flex h-screen">
      <Sidebar />

      <div className="flex-1 overflow-auto p-4">
        <div className="mb-8 mt-8 px-6">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Suivi de performance</h1>
          <p className="text-lg text-gray-600">Vue globale centrée sur les hôtels Live</p>
        </div>

        <div className="mb-4 flex flex-col gap-3 px-6 md:flex-row md:items-center md:justify-between">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un hôtel..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none md:w-80"
          />
          <div className="flex items-center justify-end gap-6">
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap text-xs font-medium text-gray-600" htmlFor="monthPickerGlobal">
                Mois
              </label>
              <input
                id="monthPickerGlobal"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-2 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none"
              />
            </div>
            <p className="text-sm text-gray-600">{`${totalDisplayed} hôtels affichés`}</p>
          </div>
        </div>

        <div className="mb-8 space-y-3 px-6">
          <h2 className="text-lg font-semibold text-gray-900">Hôtels Live ({liveHotels.length})</h2>
          <div className="overflow-hidden rounded-lg bg-white shadow-md">
            <PerformanceHotelsTable
              hotels={liveHotels}
              showSynthesisRow
              synthesis={liveSynthesis}
              sortConfig={liveSortConfig}
              onSort={handleLiveSort}
              onNavigateHotel={handleNavigateHotel}
            />
          </div>
        </div>

        <div className="space-y-3 px-6 pb-8">
          <h2 className="text-lg font-semibold text-gray-900">Autres hôtels ({otherHotels.length})</h2>
          <p className="text-sm text-gray-500">Waiting, Fini, Pas équipé — affichage informatif sans synthèse.</p>
          <div className="overflow-hidden rounded-lg bg-white shadow-md">
            <PerformanceHotelsTable
              hotels={otherHotels}
              showStatusColumn
              sortConfig={otherSortConfig}
              onSort={handleOtherSort}
              onNavigateHotel={handleNavigateHotel}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerformancesPage;
